// Meeting utility functions and shared components
import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return '0:00';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const formatTime = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true
  });
};

// =============================================================================
// MEETING CLOCK COMPONENT
// =============================================================================

export const MeetingClock = ({ serverElapsed = 0, isLive, isPaused = false }) => {
  const [elapsed, setElapsed] = useState(serverElapsed);
  const [localOffset, setLocalOffset] = useState(0);
  
  // Sync with server elapsed time when it changes
  useEffect(() => {
    setElapsed(serverElapsed);
    setLocalOffset(0);
  }, [serverElapsed]);
  
  // Only tick locally when live and NOT paused
  useEffect(() => {
    if (!isLive || isPaused) {
      return;
    }
    
    const interval = setInterval(() => {
      setLocalOffset(prev => prev + 1);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isLive, isPaused]);
  
  // Combined elapsed = server elapsed + local offset (only when not paused)
  const displayElapsed = isPaused ? elapsed : elapsed + localOffset;
  
  // Determine styling based on state
  let bgClass = 'bg-gray-100 text-gray-600';
  let statusIndicator = null;
  
  if (isLive && isPaused) {
    bgClass = 'bg-amber-50 text-amber-700 border border-amber-300';
    statusIndicator = (
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 bg-amber-500 rounded-full" />
        <span className="text-sm font-medium">PAUSED</span>
      </span>
    );
  } else if (isLive) {
    bgClass = 'bg-red-50 text-red-700';
    statusIndicator = (
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
        <span className="text-sm font-medium">LIVE</span>
      </span>
    );
  }
  
  return (
    <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${bgClass}`}>
      <Clock className="h-5 w-5" />
      <span className="font-mono text-lg font-semibold">{formatDuration(displayElapsed)}</span>
      {statusIndicator}
    </div>
  );
};
